import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface ApiAnswer {
  text: string;
  index: number; // Correct position (1, 2, 3...)
  id_internal: number;
}

interface UserAnswer {
  id_internal: number;
  index: number; // User's position
}

interface QuizSortData {
  question: string;
  answers: ApiAnswer[];
  explanation_true_text?: string;
  explanation_false_text?: string;
}

interface QuizSortDetailProps {
  data: {
    list_answer_quizz_sort?: UserAnswer[];
  };
  contentId?: number;
}

const QuizSortDetail = ({ data, contentId }: QuizSortDetailProps) => {
  const { t } = useTranslation();
  const [quizData, setQuizData] = useState<QuizSortData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const userAnswers = data.list_answer_quizz_sort || [];

  // Fetch content-quizz-sort data
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.stats.contentSpecificData}?contentId=${contentId}&contentType=content-quizz-sort`,
        );

        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        const sortData = response.data;

        if (sortData) {
          setQuizData({
            question: sortData.question || "",
            answers: sortData.answers || [],
            explanation_true_text: sortData.explanation_true_text,
            explanation_false_text: sortData.explanation_false_text,
          });
        }
      } catch (err) {
        console.error("Error fetching quiz sort content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!quizData || quizData.answers.length === 0) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Get user's position for a given id_internal
  const getUserPosition = (idInternal: number): number | null => {
    const userAnswer = userAnswers.find((a) => a.id_internal === idInternal);
    if (!userAnswer) return null;
    // Return index only if it's a valid positive number (> 0, since positions start at 1)
    // -1 typically means "not answered"
    return userAnswer.index > 0 ? userAnswer.index : null;
  };

  // Sort answers by display order (id_internal)
  const sortedAnswers = [...quizData.answers].sort((a, b) => a.id_internal - b.id_internal);

  // Check if all answers are correct
  const allCorrect = sortedAnswers.every((answer) => {
    const userPos = getUserPosition(answer.id_internal);
    return userPos === answer.index;
  });

  const explanation = (
    allCorrect ? quizData.explanation_true_text : quizData.explanation_false_text
  )?.trim();

  return (
    <div className="h-full flex flex-col">
      <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
        {/* Question header */}
        <div className="mb-4">
          <span className="text-sm font-medium text-gray-500">
            {t("statsNew.interaction.quizSort.answers")}
          </span>
          <p className="mt-2 font-medium text-gray-900">{quizData.question}</p>
        </div>

        {/* Column header for correct answer */}
        <div className="flex items-center gap-3 px-4 py-2 text-xs text-gray-500 uppercase tracking-wide">
          <span className="flex-1" />
          <span className="flex-shrink-0">{t("statsNew.interaction.quizSort.correctAnswer")}</span>
        </div>

        {/* Answers list */}
        <div className="space-y-2">
          {sortedAnswers.map((answer) => {
            const userPosition = getUserPosition(answer.id_internal);
            const isCorrect = userPosition === answer.index;

            return (
              <div
                key={answer.id_internal}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  isCorrect ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                }`}
              >
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {isCorrect ? (
                    <svg
                      className="h-5 w-5 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>

                {/* User's position (left) */}
                <span
                  className={`flex-shrink-0 font-bold ${
                    isCorrect ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {userPosition ?? "-"}
                </span>

                {/* Answer text */}
                <span className="flex-1 text-sm text-gray-900">{answer.text}</span>

                {/* Correct position (right) */}
                <span className="flex-shrink-0 text-sm text-gray-500 italic">{answer.index}</span>
              </div>
            );
          })}
        </div>

        {/* Explanation */}
        {explanation && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-1">
              {t("statsNew.interaction.explanation")}
            </p>
            <div className={`p-3 rounded-lg ${allCorrect ? "bg-green-50" : "bg-red-50"}`}>
              <p className={`text-sm ${allCorrect ? "text-green-700" : "text-red-700"}`}>
                {explanation}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizSortDetail;
